import { Icon } from '@assembly-js/design-system';
import clsx from 'clsx';
import {
  KeyboardEvent,
  ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

/** Soft fade on the text itself so the line-clamp ellipsis stays visible. */
const PROMPT_OVERFLOW_MASK =
  'linear-gradient(to bottom, #000 55%, rgba(0, 0, 0, 0.5) 100%)';

const OVERFLOW_TOLERANCE_PX = 1;

/** Figma context tokens (node 12170:117983 / 12170:117947). */
const CONTEXT_WELL_BG_CLASS = 'bg-[#eeeff1]';
const CONTEXT_MUTED_TEXT_CLASS = 'text-[#5f636a]';
// Studio pages inherit PP Mori via `font-studio`; Figma specs Inter Regular here.
const CONTEXT_FONT_CLASS = 'font-["Inter"] font-normal';
// Preflight is disabled in this app — `border` alone sets width but not style,
// so without `border-solid` the card outline never paints.
const CONTEXT_CARD_CLASS = clsx(
  // overflow-hidden keeps the well inside the padded border; children must not
  // use w-full (100% of the padded parent overflows the content box).
  'box-border flex w-full min-w-0 flex-col gap-2 overflow-hidden rounded-[12px] border border-solid border-[#dfe1e4] bg-white p-3',
  CONTEXT_FONT_CLASS,
);

const CONTEXT_WELL_CLASS = clsx(
  'flex min-w-0 flex-col rounded-lg px-3.5 py-3',
  CONTEXT_WELL_BG_CLASS,
);

export interface StudioArrivalContextProps {
  prompt?: string;
  templateName?: string;
  templateDescription?: string;
}

const ArrivalLabel = ({ children }: { children: string }) => (
  <p
    className={clsx(
      'm-0 min-w-0 pl-1 text-[13px] leading-[1.5]',
      CONTEXT_MUTED_TEXT_CLASS,
    )}
  >
    {children}
  </p>
);

const ContextCard = ({ children }: { children: ReactNode }) => (
  <div className={CONTEXT_CARD_CLASS}>{children}</div>
);

const PromptArrivalContext = ({ prompt }: { prompt: string }) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const promptRef = useRef<HTMLParagraphElement>(null);

  useLayoutEffect(() => {
    setIsExpanded(false);
  }, [prompt]);

  useEffect(() => {
    const element = promptRef.current;
    if (!element) {
      return;
    }

    const measureOverflow = () => {
      setIsOverflowing(
        element.scrollHeight > element.clientHeight + OVERFLOW_TOLERANCE_PX,
      );
    };

    measureOverflow();

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver(measureOverflow);
    observer.observe(element);
    return () => observer.disconnect();
  }, [prompt, isExpanded]);

  const isInteractive = isOverflowing || isExpanded;
  const showMask = isOverflowing;

  const toggleExpanded = () => {
    if (!isInteractive) {
      return;
    }
    setIsExpanded((prev) => !prev);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleExpanded();
    }
  };

  return (
    <ContextCard>
      <ArrivalLabel>
        {t(($) => $.page_onboarding.studio.arrival.prompt_label)}
      </ArrivalLabel>
      <div
        {...(isInteractive
          ? {
              role: 'button' as const,
              tabIndex: 0,
              'aria-expanded': isExpanded,
              onClick: toggleExpanded,
              onKeyDown: handleKeyDown,
            }
          : {})}
        className={clsx(
          CONTEXT_WELL_CLASS,
          'gap-2',
          isInteractive && 'cursor-pointer',
        )}
      >
        <p
          ref={promptRef}
          style={{
            maskImage: showMask ? PROMPT_OVERFLOW_MASK : undefined,
            WebkitMaskImage: showMask ? PROMPT_OVERFLOW_MASK : undefined,
          }}
          className={clsx(
            'm-0 min-w-0 whitespace-pre-wrap break-words text-[15px] leading-[1.6] text-black',
            isExpanded ? 'line-clamp-6' : 'line-clamp-2',
          )}
        >
          {prompt}
        </p>
        {isInteractive ? (
          <span
            className={clsx(
              'flex w-fit items-center gap-1 text-[13px] leading-[1.5]',
              CONTEXT_MUTED_TEXT_CLASS,
            )}
          >
            {isExpanded
              ? t(($) => $.page_onboarding.studio.arrival.show_less)
              : t(($) => $.page_onboarding.studio.arrival.show_more)}
            <Icon
              icon="ChevronDown"
              className={clsx(
                'h-3 w-3 transition-transform',
                isExpanded && 'rotate-180',
              )}
            />
          </span>
        ) : null}
      </div>
    </ContextCard>
  );
};

const TemplateArrivalContext = ({
  templateName,
  templateDescription,
}: {
  templateName: string;
  templateDescription?: string;
}) => {
  const { t } = useTranslation();

  return (
    <ContextCard>
      <ArrivalLabel>
        {t(($) => $.page_onboarding.studio.arrival.template_label)}
      </ArrivalLabel>
      <div className={clsx(CONTEXT_WELL_CLASS, 'gap-0.5')}>
        <p className="m-0 min-w-0 text-[15px] leading-[1.6] text-black">
          {templateName}
        </p>
        {templateDescription ? (
          <p
            className={clsx(
              'm-0 min-w-0 text-[13px] leading-[1.5]',
              CONTEXT_MUTED_TEXT_CLASS,
            )}
          >
            {templateDescription}
          </p>
        ) : null}
      </div>
    </ContextCard>
  );
};

/**
 * Shows what the visitor arrived with from marketing — a composer prompt or a
 * picked template. Prompt wins when both are present.
 */
export const StudioArrivalContext = ({
  prompt,
  templateName,
  templateDescription,
}: StudioArrivalContextProps) => {
  const trimmedPrompt = prompt?.trim();
  if (trimmedPrompt) {
    return <PromptArrivalContext prompt={trimmedPrompt} />;
  }

  const trimmedName = templateName?.trim();
  if (trimmedName) {
    return (
      <TemplateArrivalContext
        templateName={trimmedName}
        templateDescription={templateDescription?.trim() || undefined}
      />
    );
  }

  return null;
};
